import { useEffect, useState } from "react";
import AdminListToolkit from "../../components/listing/AdminListToolkit";
import AdminDataModal from "../../components/AdminDataModal";
import type { FormField } from "../../components/AdminDataModal";
import { useMessageModal } from "../../contexts/MessageModalContext";

export default function EmailVariables() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     FETCH VARIABLES
  ========================= */

  const fetchVariables = async () => {
    const res = await fetch(`${BASE_URL}/admin/settings/email-variables`, {
      credentials: "include",
    });

    const result = await res.json();
    const freshData = result.data || [];

    if (result.success) {
      setData(freshData);
      setSelectedRow((prev: any) =>
        prev
          ? freshData.find((item: any) => item.id === prev.id) || null
          : null,
      );

      setActiveRow((prev: any) =>
        prev
          ? freshData.find((item: any) => item.id === prev.id) || null
          : null,
      );
    }
  };

  useEffect(() => {
    fetchVariables();
  }, []);

  /* =========================
     SUBMIT
  ========================= */

  const handleSubmit = async (formData: any) => {
    const method = modalMode === "add" ? "POST" : "PUT";

    const url =
      modalMode === "add"
        ? `${BASE_URL}/admin/settings/email-variables`
        : `${BASE_URL}/admin/settings/email-variables/${activeRow.id}`;

    const res = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(formData),
    });

    const result = await res.json();

    if (result.success) {
      fetchVariables();
      setModalOpen(false);
      setActiveRow(null);
    }
  };

  /* =========================
     DELETE
  ========================= */

  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Variable",
      message: `Delete "${row.name}"?`,
      confirmText: "Delete",
      onConfirm: async () => {
        await fetch(`${BASE_URL}/admin/settings/email-variables/${row.id}`, {
          method: "DELETE",
          credentials: "include",
        });

        fetchVariables();
      },
    });
  };

  /* =========================
     FORM FIELDS
  ========================= */

  const variableFields: FormField[] = [
    { key: "id", label: "ID", colSpan: 2 },

    { key: "name", label: "Variable Name", colSpan: 2 },

    { key: "placeholder", label: "Placeholder", colSpan: 2 },

    { key: "description", label: "Description", type: "textarea", colSpan: 2 },
  ];

  const columns = [
    { key: "name", label: "Name" },
    { key: "placeholder", label: "Placeholder" },
    { key: "description", label: "Description" },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Email Variables"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");
          setActiveRow(null);
          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={false}
      >
        {(filteredData) => (
          <div className="algorithm-grid">
            {filteredData.map((item: any) => (
              <div
                key={item.id}
                className={`algorithm-card ${
                  selectedRow?.id === item.id ? "selected" : ""
                }`}
                onClick={() => setSelectedRow(item)}
              >
                <h3>{item.name}</h3>

                <p>
                  <strong>Placeholder:</strong> {item.placeholder}
                </p>

                <p>{item.description}</p>
              </div>
            ))}
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Email Variable"
        fields={variableFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
